import gql from 'graphql-tag'
import memoize from 'lodash.memoize'
import React from 'react'
import { Route } from '../../graphql/schema/types/route'
import { RouteCoverageOrderField } from '../../graphql/schema/types/route-coverage-order'
import { useApplicationInfo } from '../vulnerabilities-grid/api/use-application-info'
import { Grid, MaxCellHeightsByRow } from '../../legacy-design-system/grid'
import { GridLoader, GridStateManager } from '../grid-data'
import {
  LoadingTextMask,
  LoadingTextMaskWidthFactor,
  LoadingMaskRectangle
} from '../../legacy-design-system/loading-masks'
import { Order, OrderDirection } from '../../legacy-design-system/order'
import { RouteContext, useSessionFiltersSync } from '../route-provider'
import { getApplicationDrilldownID } from '../route/current-route'
import { getRouteCellRenderer } from './route-cell'
import { RouteQuery } from './route-query'
import {
  getEnvironmentsCellRenderer,
  loadingEnvironmentsCell
} from './environments-cell'
import { getServerCellRenderer, loadingServerCellRenderer } from './server-cell'
import {
  routeFragment,
  orderFieldConfig,
  getRowHeightFromServerCount,
  maxServersShownByDefault
} from './shared'
import { getVulnerabilitiesCellRenderer } from './vulnerabilities-cell'
import { getApplicationCellRenderer } from './application-cell'
import { ApplicationLoaderCacheFirst } from '../application-loader'
import { getRowActionsCellRenderer } from './row-actions-cell'
import { RouteCoverageChart, RouteCoverageSummary } from '../routes-coverage'
import { RoutesFiltersType } from '../routes-coverage/chart'
import { getDiscoveredCellRenderer } from './discovered-cell'
import { getLastActivityCellRenderer } from './last-activity-cell'
import { useDateRange } from '../vulnerabilities-grid/use-date-range'
import { getDiscoveredHeaderRenderer } from './discovered-header'
import { getLastActivityHeaderRenderer } from './last-activity-header'
import { getStatusCellRenderer } from './status-cell'
import { BulkActionList } from './actions/bulk-action-list'
import { LegacyGridContainer } from '../../legacy-design-system/legacy-main'
import { RouteSnapshotLoader } from './route-snapshot-loader'
import {
  RouteCoverageEmptyStateNoRoutes,
  RouteCoverageEmptyStateUnlicensed
} from './empty-state'
import { AggregationConfigs, AggregationOptions } from '../aggregations'
import {
  OnGroupToggleProps,
  OnOptionToggleProps
} from '../../legacy-design-system/filter'
import { useTotalApplicationCustomAgentSessions } from '../session-metadata/api/total-application-custom-agent-sessions'

const routeCoverageBatchSize = 25

function genericLoadingCellRenderer(
  min: LoadingTextMaskWidthFactor,
  max: LoadingTextMaskWidthFactor
) {
  return <LoadingTextMask min={min} max={max} />
}

interface RowQueryProps {
  readonly rowDataID: string
  readonly children: (rowData: Route) => React.ReactElement
}

const getRouteQuery = memoize(
  () =>
    function RouteCoverageRowQuery({ rowDataID, children }: RowQueryProps) {
      return <RouteQuery id={rowDataID} children={(route) => children(route)} />
    }
)

export const routeQuickFiltersQuery = gql`
  query RouteQuickFilters(
    $organizationID: ID!
    $applicationID: String!
    $filterText: String
    $quickFilter: String
    $dateFilter: RouteDateFilter
    $discoveredDateFilter: RouteDateFilter
    $applications: [String]
    $agentSessionId: [String]
  ) {
    routeQuickFilters(
      organizationID: $organizationID
      applicationID: $applicationID
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        dateFilter: $dateFilter
        discoveredDateFilter: $discoveredDateFilter
        applications: $applications
        agentSessionId: $agentSessionId
      }
    ) {
      id
      name
      count
    }
  }
`

export const routePageQuery = gql`
  query RouteCoveragePage(
    $organizationID: ID!
    $applicationID: ID!
    $offset: Int!
    $limit: Int!
    $orderBy: RouteCoverageOrder!
    $filterText: String
    $quickFilter: String
    $dateFilter: RouteDateFilter
    $discoveredDateFilter: RouteDateFilter
    $applications: [String]
    $agentSessionId: [String]
  ) {
    routeCoverage(
      organizationID: $organizationID
      applicationID: $applicationID
      offset: $offset
      limit: $limit
      orderBy: $orderBy
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        dateFilter: $dateFilter
        discoveredDateFilter: $discoveredDateFilter
        applications: $applications
        agentSessionId: $agentSessionId
      }
    ) {
      items {
        ...Route
      }
      total
      globalTotal
      exercisedCount
      totalCountExceptSessionMetadataFilter
      exercisedCountExceptSessionMetadataFilter
    }
  }

  ${routeFragment}
`

export type RouteAggregationCategory = 'applications'

const initialAggregationSelections: AggregationOptions<RouteAggregationCategory> =
  {
    applications: []
  }

const aggregationConfigs: AggregationConfigs<RouteAggregationCategory> = {
  applications: {
    groupName: {
      id: 'title_applications'
    },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { name } }) => name
  }
}

const getRouteAggregationQuery = (name: string, queryFragment: string) =>
  gql`
    query Route${name}Aggregations(
      $organizationID: ID!
      $applicationID: ID!
      $filterText: String
      $quickFilter: String
      $dateFilter: RouteDateFilter
      $discoveredDateFilter: RouteDateFilter
      $applications: [String]
      $agentSessionId: [String]
    ) {
      routeAggregations(
        organizationID: $organizationID
        applicationID: $applicationID
        filters: {
          filterText: $filterText
          quickFilter: $quickFilter
          dateFilter: $dateFilter
          discoveredDateFilter: $discoveredDateFilter
          applications: $applications
          agentSessionId: $agentSessionId
        }
      ) {
        ${queryFragment}
      }
    }
  `

const aggregationQueries = [
  getRouteAggregationQuery(
    'Applications',
    `applications {
    count
    key {
      id
      name
    }
  }`
  )
]

const getSessionMetadataFieldsQuery = memoize(
  (id) => gql`
    query ApplicationSessionMetadataFields(
      $organizationID: ID!
    ) {
      sessionMetadataAppFields(
        organizationID: $organizationID
        applicationID: "${id}"
      ) {
        id
        displayLabel
        agentLabel
        count
      }
    }
  `
)

export function quickFilterToGroup({ id }) {
  if (id === 'EXCLUDED') {
    return 'excluded'
  }

  return 'other'
}

export const errorUnableToDetermineOrgID =
  'Application route coverage grid failed to load because organization UUID could not be determined.'
export const errorUnableToDetermineAppID =
  'Application route coverage grid failed to load because application UUID could not be determined.'

export interface ContextValue {
  order: Order<RouteCoverageOrderField>
  filterText: string
  quickFilterID: string
  onSetSortField: (field: RouteCoverageOrderField) => void
  onSetSortDirection: (direction: OrderDirection) => void
  onFilterTextChange: (vaslue: string) => void
  onQuickFilterIDChange: (quickFilterID: string) => void
  updateCellHeight: (rowDataID: string, height: number) => void
  registerListRef: (ref: any) => void
  maxCellHeightsByRow: MaxCellHeightsByRow
  recomputeRowHeights: () => void
  resetFilters: () => void
  aggregationSelections: AggregationOptions<RouteAggregationCategory>
  onAggregationCategoriesClear: (categories: RouteAggregationCategory[]) => void
  onGroupToggle: (props: OnGroupToggleProps<RouteAggregationCategory>) => void
  onOptionToggle: (props: OnOptionToggleProps<RouteAggregationCategory>) => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const RouteCoverageGridContext = React.createContext<ContextValue>({
  order: {
    field: 'EXERCISED',
    direction: orderFieldConfig.EXERCISED.direction
  },
  onSetSortField: noop,
  onSetSortDirection: noop,
  filterText: '',
  onFilterTextChange: noop,
  quickFilterID: 'ALL',
  onQuickFilterIDChange: noop,
  updateCellHeight: noop,
  registerListRef: noop,
  maxCellHeightsByRow: {},
  recomputeRowHeights: noop,
  resetFilters: noop,
  aggregationSelections: initialAggregationSelections,
  onAggregationCategoriesClear: noop,
  onGroupToggle: noop,
  onOptionToggle: noop
})

export function RouteCoverageGrid() {
  const { parameters, organizationID } = React.useContext(RouteContext)
  const applicationUuid = getApplicationDrilldownID(parameters)

  useSessionFiltersSync('application-routes')

  if (!organizationID) {
    throw new Error(errorUnableToDetermineOrgID)
  }

  if (!applicationUuid) {
    throw new Error(errorUnableToDetermineAppID)
  }

  // get app name for session metadata modal
  const { isLoading, data: appInfo } = useApplicationInfo({
    applicationUuid,
    organizationUuid: organizationID
  })

  const { data: totalAppCustomAgentSession } =
    useTotalApplicationCustomAgentSessions({
      applicationUuid,
      organizationUuid: organizationID
    })

  const {
    dateFilter,
    dateRangeOption,
    dateRange,
    onSetCustomDateRange,
    onSetDateRangeOption,
    onClearDateRange
  } = useDateRange()

  const {
    dateFilter: discoveredDateFilter,
    dateRangeOption: discoveredDateRangeOption,
    dateRange: discoveredDateRange,
    onSetCustomDateRange: discoveredOnSetCustomDateRange,
    onSetDateRangeOption: discoveredOnSetDateRangeOption,
    onClearDateRange: discoveredOnClearDateRange
  } = useDateRange()

  return isLoading ? null : (
    <ApplicationLoaderCacheFirst
      organizationID={organizationID}
      applicationID={applicationUuid}
      renderLoading={() => {
        return null
      }}
      renderLoaded={(application) => {
        if (!application.license) {
          return <RouteCoverageEmptyStateUnlicensed />
        }

        let additionalFilters: {
          [key: string]: any
        } = {
          applicationID: applicationUuid
        }

        if (dateFilter) {
          additionalFilters = {
            ...additionalFilters,
            dateFilter: {
              start: dateFilter.start,
              end: dateFilter.end
            }
          }
        }

        if (discoveredDateFilter) {
          additionalFilters = {
            ...additionalFilters,
            discoveredDateFilter: {
              start: discoveredDateFilter.start,
              end: discoveredDateFilter.end
            }
          }
        }

        return (
          <RouteCoverageGridContext.Consumer>
            {({
              order,
              onSetSortField,
              onSetSortDirection,
              filterText,
              onFilterTextChange,
              onQuickFilterIDChange,
              quickFilterID,
              updateCellHeight,
              maxCellHeightsByRow,
              recomputeRowHeights,
              registerListRef,
              aggregationSelections,
              onGroupToggle,
              onOptionToggle,
              onAggregationCategoriesClear
            }) => {
              const startDate = dateRange && dateRange.start
              const endDate = dateRange && dateRange.end
              const discoveredStartDate =
                discoveredDateRange && discoveredDateRange.start
              const discoveredEndDate =
                discoveredDateRange && discoveredDateRange.end

              return (
                <RouteSnapshotLoader
                  organizationUuid={organizationID}
                  applicationUuid={applicationUuid}
                  startDate={startDate}
                  endDate={endDate}
                >
                  {({ snapshotLoading, snapshotData }) => (
                    <GridLoader<
                      Route,
                      RouteCoverageOrderField,
                      RouteAggregationCategory,
                      {
                        exercisedCount?: number
                        totalCountExceptSessionMetadataFilter?: number
                        exercisedCountExceptSessionMetadataFilter?: number
                      }
                    >
                      aggregationsQueries={aggregationQueries}
                      aggregationsQueryRootKey="routeAggregations"
                      aggregationBucketIDConfigs={aggregationConfigs}
                      aggregationFilters={aggregationSelections}
                      batchSize={routeCoverageBatchSize}
                      collectionQuery={routePageQuery}
                      collectionQueryRootKey="routeCoverage"
                      filterText={filterText}
                      order={order}
                      applicationID={applicationUuid}
                      quickFiltersQuery={routeQuickFiltersQuery}
                      quickFiltersQueryRootKey="routeQuickFilters"
                      quickFilterID={quickFilterID}
                      additionalFilters={additionalFilters}
                      sessionMetadataFieldsQuery={getSessionMetadataFieldsQuery(
                        applicationUuid
                      )}
                      sessionMetadataFieldsQueryRootKey="sessionMetadataAppFields"
                    >
                      {({
                        total,
                        totalRowCount,
                        additionalCollectionProps,
                        globalTotal,
                        loadMore,
                        assessLicenseBreakdown,
                        loadedRowCount,
                        rowDataValue,
                        rowGetter,
                        isRowLoaded,
                        quickFilters,
                        refreshGridData,
                        aggregations,
                        sessionMetadataFields
                      }) => {
                        if (globalTotal === 0) {
                          return <RouteCoverageEmptyStateNoRoutes />
                        }

                        const RowQuery = getRouteQuery()
                        const onSuccess = () => refreshGridData()
                        const sessionMetadatActionProps = {
                          appName: appInfo?.application?.name,
                          applicationUuid,
                          organizationUuid: organizationID,
                          filterFields: sessionMetadataFields,
                          filtersActive: aggregationSelections,
                          filterToggle: onOptionToggle,
                          isRouteCoverageGrid: true,
                          totalAppCustomAgentSession
                        }

                        return (
                          <LegacyGridContainer>
                            <Grid<
                              Route,
                              RouteCoverageOrderField,
                              RouteAggregationCategory
                            >
                              RowQuery={RowQuery}
                              loadedRowCount={loadedRowCount}
                              rowDataValue={rowDataValue}
                              gridDataValue={assessLicenseBreakdown}
                              maxCellHeightsByRow={maxCellHeightsByRow}
                              filterValue={{}}
                              isRowLoaded={isRowLoaded}
                              isRowSelectable={() => true}
                              rowGetter={rowGetter}
                              loadMore={loadMore}
                              totalRowCount={totalRowCount}
                              onQuickFilterIDChange={onQuickFilterIDChange}
                              updateCellHeight={updateCellHeight}
                              quickFilterID={quickFilterID}
                              quickFilters={quickFilters}
                              quickFilterToGroup={quickFilterToGroup}
                              aggregations={aggregations}
                              aggregationConfigs={aggregationConfigs}
                              aggregationSelections={aggregationSelections}
                              onGroupToggle={onGroupToggle}
                              onOptionToggle={onOptionToggle}
                              onAggregationCategoriesClear={
                                onAggregationCategoriesClear
                              }
                              searchPlaceholderMessage={{
                                id: 'placeholder_findRoute'
                              }}
                              recomputeRowHeights={recomputeRowHeights}
                              rowHeight={(route) => {
                                const rowHeightFromServerCount =
                                  getRowHeightFromServerCount(
                                    Math.min(
                                      route && route.servers
                                        ? route.servers.length
                                        : 0,
                                      maxServersShownByDefault
                                    )
                                  )
                                if (route && route.id) {
                                  return (
                                    maxCellHeightsByRow[route.id] ||
                                    rowHeightFromServerCount
                                  )
                                }
                                return rowHeightFromServerCount
                              }}
                              renderBulkActions={(rowSelectionProps) => (
                                <BulkActionList
                                  onSuccess={onSuccess}
                                  organizationID={organizationID}
                                  applicationRole={application.role}
                                  applicationID={applicationUuid}
                                  startDate={startDate}
                                  endDate={endDate}
                                  discoveredStartDate={discoveredStartDate}
                                  discoveredEndDate={discoveredEndDate}
                                  {...rowSelectionProps}
                                />
                              )}
                              order={order}
                              onSetSortField={(value) => {
                                onSetSortField(value)
                              }}
                              onSetSortDirection={onSetSortDirection}
                              registerListRef={registerListRef}
                              orderFieldConfig={orderFieldConfig}
                              onFilterTextChange={(value) => {
                                onFilterTextChange(value)
                              }}
                              filterText={filterText}
                              batchSize={routeCoverageBatchSize}
                              columns={[
                                {
                                  cellRenderer: getRouteCellRenderer({
                                    organizationUuid: organizationID,
                                    applicationUuid,
                                    aggregationSelections
                                  }),
                                  loadingCellRenderer: () =>
                                    genericLoadingCellRenderer(7, 9),
                                  headerConfig: {
                                    message: {
                                      id: 'title_route'
                                    }
                                  },
                                  width: 700,
                                  minWidth: 100,
                                  maxWidth: 1000,
                                  horizontalCenter: false
                                },
                                {
                                  cellRenderer: getEnvironmentsCellRenderer(),
                                  loadingCellRenderer: loadingEnvironmentsCell,
                                  headerConfig: {
                                    message: { id: 'title_environment_server' }
                                  },
                                  width: 175,
                                  minWidth: 175,
                                  maxWidth: 175,
                                  horizontalCenter: false
                                },
                                {
                                  cellRenderer: getServerCellRenderer(),
                                  loadingCellRenderer:
                                    loadingServerCellRenderer,
                                  headerConfig: {
                                    message: {
                                      id: 'title_server'
                                    }
                                  },
                                  width: 400,
                                  minWidth: 100,
                                  maxWidth: 750,
                                  horizontalCenter: false
                                },
                                {
                                  cellRenderer:
                                    getVulnerabilitiesCellRenderer(),
                                  loadingCellRenderer: () =>
                                    genericLoadingCellRenderer(3, 5),
                                  headerConfig: {
                                    message: {
                                      id: 'title_vulnerabilities'
                                    }
                                  },
                                  width: 200,
                                  minWidth: 150,
                                  maxWidth: 250,
                                  horizontalCenter: true
                                },
                                {
                                  cellRenderer: getApplicationCellRenderer(),
                                  loadingCellRenderer: () =>
                                    genericLoadingCellRenderer(3, 5),
                                  headerConfig: {
                                    message: {
                                      id: 'title_application',
                                      defaultMessage: 'Application'
                                    },
                                    aggregations: ['applications']
                                  },
                                  width: 150,
                                  minWidth: 150,
                                  maxWidth: 250,
                                  horizontalCenter: false
                                },
                                {
                                  cellRenderer: getDiscoveredCellRenderer(),
                                  loadingCellRenderer: () =>
                                    genericLoadingCellRenderer(3, 5),
                                  headerConfig: {
                                    message: {
                                      id: 'title_fistSeen_server'
                                    },
                                    // this could be generic getDateRangeHeaderRenderer
                                    // prettier-ignore
                                    render: getDiscoveredHeaderRenderer({
                                      dateRange: discoveredDateRange,
                                      dateRangeOption: discoveredDateRangeOption,
                                      onSetCustomDateRange: discoveredOnSetCustomDateRange,
                                      onSetDateRangeOption: discoveredOnSetDateRangeOption,
                                      onClearDateRange: discoveredOnClearDateRange
                                    })
                                  },
                                  width: 150,
                                  minWidth: 150,
                                  maxWidth: 250,
                                  horizontalCenter: false
                                },
                                {
                                  cellRenderer: getLastActivityCellRenderer(),
                                  loadingCellRenderer: () =>
                                    genericLoadingCellRenderer(3, 5),
                                  headerConfig: {
                                    message: {
                                      id: 'title_route_lastActivity'
                                    },
                                    render: getLastActivityHeaderRenderer({
                                      dateRange,
                                      dateRangeOption,
                                      onSetCustomDateRange,
                                      onSetDateRangeOption,
                                      onClearDateRange
                                    })
                                  },
                                  width: 150,
                                  minWidth: 150,
                                  maxWidth: 250,
                                  horizontalCenter: false
                                },
                                {
                                  cellRenderer: getStatusCellRenderer(),
                                  loadingCellRenderer: () =>
                                    genericLoadingCellRenderer(3, 3),
                                  headerConfig: {
                                    message: {
                                      id: 'title_status'
                                    }
                                  },
                                  width: 100,
                                  minWidth: 100,
                                  maxWidth: 200,
                                  horizontalCenter: false
                                },
                                {
                                  cellRenderer: getRowActionsCellRenderer({
                                    onSuccess,
                                    organizationId: organizationID,
                                    applicationUuid,
                                    applicationRole: application.role,
                                    aggregationSelections
                                  }),
                                  loadingCellRenderer: () => <div />,
                                  maxWidth: 100,
                                  width: 100,
                                  minWidth: 50
                                }
                              ]}
                              contentBelowHeader={
                                <>
                                  <RouteCoverageSummary
                                    stats={{
                                      total,
                                      ...additionalCollectionProps
                                    }}
                                    sessionMetadataActionProps={
                                      sessionMetadatActionProps
                                    }
                                  />
                                  {aggregationSelections?.[
                                    'agentSessionId'
                                  ] ? null : snapshotLoading ||
                                    !snapshotData ? (
                                    <LoadingMaskRectangle
                                      height={300}
                                      colorValue="normal"
                                    />
                                  ) : (
                                    <RouteCoverageChart
                                      height={300}
                                      initialFilter={
                                        quickFilterID as RoutesFiltersType
                                      }
                                      timeline={snapshotData.timeline}
                                      routesBreakdown={
                                        snapshotData.routesBreakdown
                                      }
                                      mostRecentRoutesBreakdown={
                                        snapshotData.mostRecentRoutesBreakdown
                                      }
                                    />
                                  )}
                                </>
                              }
                              gridCellTestID="route-coverage-grid-cell"
                              isDrilldown
                            />
                          </LegacyGridContainer>
                        )
                      }}
                    </GridLoader>
                  )}
                </RouteSnapshotLoader>
              )
            }}
          </RouteCoverageGridContext.Consumer>
        )
      }}
    />
  )
}

export function RouteCoverageGridProvider({ children }) {
  return (
    <GridStateManager
      defaultOrderField="EXERCISED"
      orderFieldConfig={orderFieldConfig}
      filterType="application-routes"
    >
      {(state) => {
        return (
          <RouteCoverageGridContext.Provider value={state}>
            {children}
          </RouteCoverageGridContext.Provider>
        )
      }}
    </GridStateManager>
  )
}
